<template>
  <div class="approvalsPage">
    <h2>Approval Requests</h2>
    <div class="weekendSwc">
      <span>Pending only</span>
      <input
        type="checkbox"
        id="pending-toggle"
        v-model="showPendingOnly"
        class="weekendCheck"
      />
      <label for="pending-toggle" class="weekendLabel"></label>
    </div>
    <div v-if="loading">Loading...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else>
      <table class="approvalsTable">
        <thead>
          <tr>
            <th>Application Date</th>
            <th>Status</th>
            <th>Applicant</th>
            <th>Approver</th>
            <th>Period</th>
            <th>Type</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="app in filteredApplications"
            :key="app.id"
            :class="{ 'gray-row': app.status !== 'PENDING' }"
          >
            <td>{{ formatDateTime(app.applied_at) }}</td>
            <td>
              <select
                v-model="app.status"
                :disabled="app.originalStatus === 'REJECTED'"
              >
                <option value="PENDING">PENDING</option>
                <option value="APPROVED">APPROVED</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </td>
            <td>{{ app.username }}</td>
            <td>
              {{ app.status === "PENDING" ? "-" : app.approved_by_name || "-" }}
            </td>
            <td>
              <template v-if="Array.isArray(app.dates) && app.dates.length > 1">
                {{ formatDateUK(app.dates[0].date) }} -
                {{ formatDateUK(app.dates[app.dates.length - 1].date) }}
              </template>
              <template
                v-else-if="Array.isArray(app.dates) && app.dates.length === 1"
              >
                {{ formatDateUK(app.dates[0].date) }}
              </template>
              {{displayLeaveType(app.leave_type)}}
            </td>
            <td>
              {{ 
                app.paidcomp_type_display === "Compensatory Off"
                      ? "Holiday in Lieu"
                      : app.paidcomp_type_display === "Paid Off"
                        ? "Paid Holiday"
                        : app.paidcomp_type_display
              }}
              
            </td>
            <td>{{ app.reason || "" }}</td>
          </tr>
          <tr v-if="filteredApplications.length === 0">
            <td colspan="7">No applications to approve.</td>
          </tr>
        </tbody>
      </table>
      <button @click="saveChanges" class="submit-button">Save Changes</button>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import notificationService from "@/services/notificationService"; // Import the new service

export default {
  name: "HolidayApprovalView",
  data() {
    return {
      applications: [],
      loading: false,
      error: "",
      showPendingOnly: true,
    };
  },
  computed: {
    filteredApplications() {
      if (this.showPendingOnly) {
        return this.applications.filter(
          (app) => app.originalStatus === "PENDING"
        );
      }
      return this.applications;
    },
  },
  methods: {
    async fetchApprovals() {
      this.loading = true;
      this.error = "";
      try {
        const res = await api.get("/leave/approvals/");
        this.applications = res.data.map((app) => ({
          ...app,
          originalStatus: app.status,
        }));
      } catch (err) {
        console.error(
          "Failed to fetch approval requests:",
          err.response || err
        );
        this.error = "Failed to fetch approval requests.";
      } finally {
        this.loading = false;
      }
    },
    displayLeaveType(code) {
      switch (code) {
        case "Full":
          return "";
        case "Morning":
          return "AM";
        case "Afternoon":
          return "PM";
        default:
          return code;
      }
    },
    displayStatus(status) {
      switch (status) {
        case "PENDING":
          return "PENDING";
        case "APPROVED":
          return "APPROVED";
        case "REJECTED":
          return "REJECTED";
        default:
          return status;
      }
    },
    formatDateTime(dt) {
      if (!dt) return "";
      const d = new Date(dt);
      return d.toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatDateUK(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    async saveChanges() {
      try {
        const payload = this.filteredApplications
          .filter((app) => app.status !== app.originalStatus) // 変更があったものだけを抽出
          .map((app) => ({
            id: app.id,
            status: app.status,
          }));

        if (payload.length === 0) {
          alert("No changes to save.");
          return;
        }

        await api.put("/leave/approvals/bulk-update/", payload);
        
        // For each updated application, mark approver notifications as read
        for (const app of payload) {
          await notificationService.markApproverNotificationsAsRead(app.id);
        }

        alert("Status saved successfully.");

      } catch (err) {
        console.error("Failed to update status:", err.response || err);
        alert("Failed to save changes.");
      }
    },
  },
  async mounted() {
    const token = sessionStorage.getItem("token");
    if (token) {
      await this.fetchApprovals();
    } else {
      this.$router.push("/");
    }
  },
};
</script>

<style scoped>
    .approvalsPage {
        & h2 {
            margin-bottom: 3vh;
        }
    }
    .weekendSwc {
        margin-bottom: 1rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }
    .weekendCheck {
        margin-left: 0.5em;
    }
    .weekendLabel {
        margin-left: 0.2em;
    }
    .approvalsTable {
        width: 100%;
        border-collapse: collapse;
    }
    .approvalsTable th,
    .approvalsTable td {
        border: 1px dashed #0757a280;
        padding: 8px;
        height: 3rem;
        text-align: center;
        &:first-child {
            border-left: none;
        }
        &:last-child {
            border-right: none;
        }
    }
    tr:first-child>td{ border-top:none; }
    .approvalsTable th {
        padding: 8px;
        text-align: center;
        color: white;
        font-size: 13px;
        height: 2rem;
        min-width: 50px;
        background-color: var(--blue);
        border:none;
        &:first-child {
            border-top-left-radius: 10px;
        }
        &:last-child {
            border-top-right-radius: 10px;
        }
    }
    .approvalsTable select {
        padding: 0.2rem;
        border-radius: 4px;
    }
    .gray-row {
      background-color: #f2f2f2;
    }
    .applications-table {
        & span {
            color: var(--blue);
        }
        & .historyPart {
            border-top: 1px solid var(--lightBlue);
            margin-top: 0.5rem;
            padding-top: 0.5rem;
        }
        & .submitDate {
            color: var(--midLightBlue);
            font-size: 12px;
            margin-bottom: 0;
        }
        & .submitStatus {
            color: white;
            font-size: 12px;
            padding: 0.2rem;
            margin-right: 0.3rem;
            border-radius: 5px;
        }
        & .statusPending {
            background: var(--green);
        }
        & .statusApproved {
            background: var(--blue);
        }
        & .statusRejected {
            background: var(--red);
        }
    }
    .submit-button {
        background: var(--grad90);
        width: 100%;
        max-width: 300px;
        padding: 1rem;
        border: none;
        color: white;
        border-radius: 10px;
        margin: 1rem auto;
        display: block;
    }
    .submit-button:disabled {
        background: #cccccc;
        cursor: inherit;
    }
</style>
